import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';
import { createCoursePath } from '@/data/services/coursePathServices';
import CoursePath from '@/models/CoursePath';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';

import { updateCourse } from '@/data/services/courseServices';
import CourseForm, { CourseFormFields } from './CourseForm';

export type CourseFormServiceSuccess = {
  item: CoursePath;
  action: 'create' | 'update';
};

type CourseFormServiceProps = {
  onSuccess?: (success: CourseFormServiceSuccess) => void;
};

export default function CourseFormService({
  onSuccess,
}: CourseFormServiceProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseForm.alerts',
  });
  const { coursePath, moduleId, cache } = usePedWorkspace();

  const handleCreate = async (
    data: CourseFormFields,
  ): Promise<CourseFormServiceSuccess> => {
    const created = await createCoursePath({
      course: {
        name: data.name,
        abbreviation: data.abbreviation,
        formats: data.formats,
        businessModels: data.businessModels,
        moduleId,
      },
    });
    alert.success(t('createSuccess'));
    return { item: created, action: 'create' };
  };

  const handleUpdate = async (
    data: CourseFormFields,
  ): Promise<CourseFormServiceSuccess> => {
    if (!coursePath) throw new Error('CoursePath not found');
    const payload = {
      name: data.name,
      abbreviation: data.abbreviation,
      formats: data.formats,
      businessModels: data.businessModels,
    };
    const changedFields = buildChangedObject(coursePath.course, payload);
    if (Object.keys(changedFields).length === 0) {
      return { item: coursePath, action: 'update' };
    }
    const updatedCourse = await updateCourse(
      coursePath.course.id,
      changedFields,
    );

    const coursePathUpdated = {
      ...coursePath,
      course: updatedCourse,
      slug: updatedCourse.coursePathSlug ?? coursePath.slug,
    };
    alert.success(t('updateSuccess'));
    return { item: coursePathUpdated, action: 'update' };
  };

  const handleSubmit = async (
    data: CourseFormFields,
  ): Promise<CourseFormServiceSuccess> => {
    if (coursePath) return handleUpdate(data);
    return handleCreate(data);
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: err => alert.error(getErrorMessage(err)),
    onSuccess: async response => {
      cache?.merge(response.item);
      onSuccess?.(response);
    },
  });

  return (
    <CourseForm
      course={coursePath?.course}
      onSubmit={onSubmit}
      isLoading={isPending}
    />
  );
}
